import React, { useState, useRef, useCallback } from 'react';
import { Camera, X, ImageIcon, MessageSquare } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';

// Compress image to reduce size before upload
const compressImage = (file, maxWidth = 1200, maxHeight = 1200, quality = 0.7) => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        // Calculate new dimensions while maintaining aspect ratio
        let width = img.width;
        let height = img.height;
        
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        if (height > maxHeight) {
          width = (width * maxHeight) / height;
          height = maxHeight;
        }
        
        // Create canvas and draw compressed image
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        
        // Convert to base64 with compression
        const compressedBase64 = canvas.toDataURL('image/jpeg', quality);
        resolve(compressedBase64);
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
};

export default function PhotoSlot({ 
  slot, 
  onImageChange, 
  onDescriptionChange, 
  onAdminCommentChange,
  disabled = false,
  isAdmin = false,
  showAdminComment = false
}) {
  const [isDragging, setIsDragging] = useState(false);
  const [isCompressing, setIsCompressing] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = useCallback(async (file) => {
    if (!file || disabled) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      return;
    }

    setIsCompressing(true);
    try {
      // Compress the image before converting to base64
      const compressedBase64 = await compressImage(file, 1200, 1200, 0.7);
      onImageChange(compressedBase64);
    } catch (error) {
      console.error('Image compression failed:', error);
      // Fallback to original if compression fails
      const reader = new FileReader();
      reader.onload = (e) => {
        onImageChange(e.target.result);
      };
      reader.readAsDataURL(file);
    } finally {
      setIsCompressing(false);
    }
  }, [onImageChange, disabled]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const file = e.dataTransfer.files[0];
    handleFileSelect(file);
  }, [handleFileSelect]);

  const handleDragOver = (e) => {
    e.preventDefault();
    if (!disabled) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleClick = () => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleInputChange = (e) => {
    const file = e.target.files[0];
    handleFileSelect(file);
  };

  const handleRemoveImage = (e) => {
    e.stopPropagation();
    onImageChange(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-3" data-testid={`photo-slot-${slot.slot_id}`}>
      <div
        className={`photo-slot ${slot.image_data ? 'has-image' : ''} ${isDragging ? 'border-primary bg-primary/5' : ''} ${disabled ? 'cursor-not-allowed opacity-60' : ''}`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={handleClick}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={handleInputChange}
          className="hidden"
          disabled={disabled}
          data-testid={`photo-input-${slot.slot_id}`}
        />
        
        {slot.image_data ? (
          <div className="relative w-full h-48">
            <img
              src={slot.image_data}
              alt="Uploaded"
              className="w-full h-full object-cover rounded-md"
            />
            {!disabled && (
              <Button
                variant="destructive"
                size="icon"
                className="absolute top-2 right-2 h-8 w-8"
                onClick={handleRemoveImage}
                data-testid={`remove-image-${slot.slot_id}`}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        ) : isCompressing ? (
          <div className="text-center py-6">
            <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-primary/10 flex items-center justify-center animate-pulse">
              <Camera className="h-6 w-6 text-primary" />
            </div>
            <p className="text-sm font-medium text-primary">
              Compressing image...
            </p>
          </div>
        ) : (
          <div className="text-center py-6">
            <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-slate-200 flex items-center justify-center">
              {disabled ? (
                <ImageIcon className="h-6 w-6 text-slate-400" />
              ) : (
                <Camera className="h-6 w-6 text-slate-400" />
              )}
            </div>
            <p className="text-sm font-medium text-slate-600">
              {disabled ? 'No image' : 'Click or drag to upload'}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {disabled ? '' : 'Supports any image format'}
            </p>
          </div>
        )}
      </div>
      
      <Textarea
        placeholder="Add description..."
        value={slot.description || ''}
        onChange={(e) => onDescriptionChange(e.target.value)}
        disabled={disabled}
        className="min-h-[80px] resize-none"
        data-testid={`description-${slot.slot_id}`}
      />
      
      {/* Admin Comment - shown in red/orange for visibility */}
      {(showAdminComment || slot.admin_comment) && (
        <div className="space-y-1">
          <div className="flex items-center gap-1 text-sm text-orange-600">
            <MessageSquare className="h-4 w-4" />
            <span className="font-medium">Admin Comment</span>
          </div>
          {isAdmin ? (
            <Textarea
              placeholder="Add admin comment..."
              value={slot.admin_comment || ''}
              onChange={(e) => onAdminCommentChange && onAdminCommentChange(e.target.value)}
              className="min-h-[60px] resize-none border-orange-300 bg-orange-50 text-orange-800 placeholder:text-orange-400"
              data-testid={`admin-comment-${slot.slot_id}`}
            />
          ) : slot.admin_comment && (
            <div className="p-3 bg-orange-50 border border-orange-200 rounded-md text-orange-800 text-sm">
              {slot.admin_comment}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
